import { IHotel, minuteInSeconds } from "@vmosolutions/oc-interfaces";
import { useCallback, useEffect, useMemo } from "react";
//formik
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import {
  FormikCheckboxField,
  FormikPhoneField,
  FormikSelectField,
  FormikTextField,
} from "../../../../components/MUI5FormikComponents/FormikWrappers";

import { FormikMaskTextField } from "../../../../components/FormikComponents/FormikWrappers";
//material UI
import { Box, BoxProps, Button, Grid, styled, Tooltip, Typography } from "@mui/material";
//Yup Validation
import * as Yup from "yup";
// API calls
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IAirportOption } from "src/redux/reducers/StructuresReducer";
import AmplifyAPIWrapper, { INotifications } from "../../../../API/AmplifyAPIWrapper";
import { alpha } from "@mui/material/styles";
import { emailValidation } from "../../../../components/Admin/Validation/validation";
import { airportSearchString, updateAirportOptions } from "../../../../utils/airportHelper";
import { padNumber } from "../../../../utils/index";
import { IActiveModal } from "../../../Admin/AdminWindow";
import { currencyCodes, ICurrencyCode } from "../../Forms/currencyCodes";

interface IHotelFormProps {
  selectedHotel?: IHotel | null;
  className?: string;
  updateHotelList: (hotel: IHotel, mode: "create" | "update" | "delete") => void;
  setActiveModal: (activeModal: IActiveModal) => void;
}

// New Non Flying Assignment default values.
export const initialHotelValues: IHotel = {
  // Populated by the backend
  _id: undefined,
  createdBy: undefined,
  createdAt: undefined,

  // Required Fields
  name: undefined,
  description: undefined,
  standardRate: undefined, // number
  switchTime: undefined, // seconds
  icao: undefined, // string
  priority: undefined, // integer
  earlyCheckin: undefined, // seconds
  lateCheckout: undefined, // seconds

  // Optional Fields
  email: undefined,
  phoneNumber: undefined,
  standardRateCurrency: "CAD", // string i.d CAD
  crewType: undefined, // i.e. [P = Pilot, F = Flight Attendant, B/undefined = Both]
  minStaySeconds: undefined, // seconds
  taxRate: undefined, //TODO find out if rate is percentage format or decimal.
  hotelTravelTime: undefined, // minutes
  groundTransport: undefined, //string (ex: Limo company name)
};

type HotelFormType = FormikProps<IHotel>;

export type IHotelForm = FormikProps<IHotel>;

export const secondsToHrsMinString = (seconds: number): string => {
  if (!seconds || seconds < minuteInSeconds || isNaN(seconds)) {
    return "0000";
  }
  const hours = Math.floor(seconds / 60 / 60);
  const minutes = Math.floor(seconds / 60) - hours * 60;
  return `${hours > 99 ? "99" : padNumber(hours, 2)}${minutes > 59 ? "59" : padNumber(minutes, 2)}`;
};
export const hrsMinString2seconds = (time: string): number => {
  const [hours, minutes] = time.split(":");
  const seconds = parseInt(hours) * 3600 + parseInt(minutes) * 60;
  return seconds;
};

const minutesToHrsMinString = (mins: number): string => {
  if (!mins || isNaN(mins)) {
    return "0000";
  }
  const hours = Math.floor(mins / 60);
  const minutes = Math.floor(mins) - hours * 60;
  return `${hours > 99 ? "99" : padNumber(hours, 2)}${minutes > 59 ? "59" : padNumber(minutes, 2)}`;
};

const hrsMinString2minutes = (time: string): number => {
  const [hours, minutes] = time.split(":");
  const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
  return totalMinutes;
};

const crewTypeOptions = [
  { label: "All", value: "B" },
  { label: "Pilots", value: "P" },
  { label: "Cabin Crew", value: "F" },
];

// Hotel Form Validation with YUP
const HotelSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  description: Yup.string().required("Description is required"),
  standardRate: Yup.number().min(0, "Standard Rate must be a number above 0").required("Standard Rate is required"),
  switchTime: Yup.number().required("Switch Time is required"),
  icao: Yup.string().required("ICAO is required"),
  priority: Yup.number()
    .integer("Priority must be in the form of an integer")
    .min(0, "Priority must be a number above 0")
    .required("Priority is required"),
  earlyCheckin: Yup.number().required("Early Check-In value is required"),
  lateCheckout: Yup.number().required("Late Check-Out value is required"),
  phoneNumber: Yup.string()
    .test("len", "Phone number cannot exceed 40 characters", (val) => !val || val.length < 40)
    .nullable(),
  taxRate: Yup.number()
    .min(0, "Tax Rate must be a number between 0 and 100")
    .max(100, "Tax Rate must be a number between 0 and 100"),
  hotelTravelTime: Yup.number().moreThan(0, "Airport to Hotel travel time must be greater than 0."),
  minStaySeconds: Yup.number(),
});

interface IOption {
  label: string;
  value: string;
}

/** Convert semi-colon delineated string to label/value pairs for select field */
const convertSemiColonDelineatedStringToOptions = (emails: string): IOption[] => {
  const emailsArray = !!emails && emails?.indexOf(";") ? emails.split(";") : emails ? [emails] : [];
  const emailOptions: IOption[] = emailsArray.map((email: string) => ({ label: email, value: email }));
  return emailOptions;
};

const HotelFormWrapper: React.FC<BoxProps> = styled(Box)(({ theme }) => ({
  minWidth: 363,

  "& .form_footer": {
    marginTop: 10,
    display: "flex",
    justifyContent: "flex-start",
    "& button": { margin: "5px 5px" },
    "& .btn": {
      flexWrap: "wrap",
      display: "flex",
      height: 27,
      fontSize: 10,
      lineHeight: "12px",
      padding: "2px 8px",
      margin: "0px 4px",
      "&.submit": {
        color: theme.custom.forms.inputBack,
      },
    },
  },
  "& p.required_fields_label": {
    margin: 0,
    fontWeight: 300,
    fontSize: 10,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
  },
  "& .required_fields": {
    backgroundColor: alpha(theme.custom.forms.labels, 0.2),
    margin: "2px 0px",
    padding: 10,
  },
  "& .optional_fields": {
    margin: "2px 0px",
    padding: 10,
  },
  "& .field": {
    width: "auto",
    minWidth: 238,
    "&.copy_to_clipboard": {
      width: 40,
      margin: 5,
    },
    "&.textfield": {
      "&.commute_time": {
        width: "auto",
        minWidth: "auto",
        "& .input_control": {
          "& label": { width: "auto" },
          "& .input-base": { width: 70 },
        },
      },
      "&.revision": {
        width: 260,
        marginRight: 15,
        "& .input_control": {
          "& label": { width: 150 },
          "& .input-base": { width: 80 },
        },
      },
      "&.multiline_short": {
        width: 350,
        "& .input_control": {
          "& label": { width: "auto" },
        },
      },
      "&.multiline_long": {
        width: "100%",
        maxWidth: 540,
        "& .input_control": {
          "& label": { width: 150 },
        },
      },
      "&.hotel": {
        "&.name, &.email": { width: 300, "& label": { width: "auto" } },
        "&.description": { width: 500, "& label": { width: "auto" } },
        "&.rate, &.priority, &.taxrate, &.traveltime, &.minstay, &.switchtime, &.earlycheckin, &.latecheckout, &.traveltime":
          {
            width: "auto",
            minWidth: "auto",
            "& label": { width: "auto" },
            "& .input-base": {
              width: 85,
              height: 26,
            },
          },
      },
    },
    "&.richtextfield": {
      width: "100%",
      "& .input_control": {
        "& label": { width: "auto" },
      },
    },
    "&.selectfield": {
      "&.aircraft_type": { minWidth: 300 },
      "&.selectaircraftgroupmembers, &.sortaircraftgroupmembers": {
        width: 238,
        minWidth: 238,
        "& label": { width: "auto" },
      },
      "&.hotel": {
        "&.emails": {
          width: 300,
          "& label": { width: "auto" },
          "& .input_control .react-select__multi-value": { maxWidth: "unset" },
        },
        "&.selectfield": {
          "&.ratecurrency, &.icao": {
            width: 200,
            minWidth: 200,
            "& label": { width: "auto" },
          },
        },
      },
    },
    "&.switchfield": { flexDirection: "row", minWidth: 102 },
    "&.datetimefield": {
      "&.no_time": { minWidth: 200 },
      "&.assigned_start": {
        "& label": { width: "auto" },
      },
    },
  },
  "& .grid_item_divider": {
    marginLeft: 20,
    borderLeft: `1px solid ${theme.custom.forms.labels}`,
  },
}));

/**
 * Base Hotel Form
 * @param props - selected hotel (from ManageHotel)
 */
export const HotelForm = (props: IHotelFormProps): JSX.Element => {
  const { selectedHotel, className, updateHotelList, setActiveModal } = props;
  const dispatch = useDispatch();

  const structures = useSelector((state: IRootState) => state.structures);
  const { airportOptions } = structures;
  const airports = useMemo(() => Object.values(airportOptions), [airportOptions]);
  const initialValues = useMemo(() => (selectedHotel ? selectedHotel : initialHotelValues), [selectedHotel]);

  // Load default values for airport data so they display in the selection menus.
  const loadAirportData = useCallback(
    (icao: string) => {
      updateAirportOptions(icao, airportOptions, dispatch);
    },
    [airportOptions, dispatch]
  );

  // If a default icao value is passed in, retrieve that airport.
  useEffect(() => {
    if (initialValues?.icao) loadAirportData(initialValues?.icao);
  }, [initialValues?.icao, loadAirportData]);

  const handleSubmit = async (values: IHotel, helpers: FormikHelpers<IHotel>): Promise<void> => {
    const { setSubmitting, setStatus, resetForm } = helpers;
    const mode = values._id ? "update" : "create";
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `${mode === "update" ? "Updating" : "Creating New"} Hotel...`,
      successMessage: `Hotel Successfully ${mode === "update" ? "Updated" : "Created"}.`,
      errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} Hotel`,
    };

    //TODO Endpoint Return Type is deprecated and needs to be updated.
    interface IOldResponseTypeUpdate {
      original: IHotel;
      modified: IHotel;
    }

    const oldresponse: IOldResponseTypeUpdate | IHotel =
      mode === "create"
        ? await AmplifyAPIWrapper.Post<IHotel>("/hotel", values, notifications)
        : await AmplifyAPIWrapper.Put<any>(`/hotel/${values._id}`, values, notifications);

    const response = oldresponse.hasOwnProperty("modified")
      ? (oldresponse as IOldResponseTypeUpdate).modified
      : (oldresponse as IHotel);

    if (response?._id) {
      resetForm({ values });
      setStatus({ success: true });
      // add or update hotel from main list.
      updateHotelList(response, mode);
    } else {
      setStatus({ success: false });
    }
    setSubmitting(false);
  };

  return (
    <Formik
      className={className}
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validationSchema={HotelSchema}
    >
      {(hotelForm: HotelFormType) => {
        const { values, dirty, isValid, isSubmitting } = hotelForm;

        // Email options must be in label/value pairs format for createable to work correctly.
        const emailOptions: IOption[] = convertSemiColonDelineatedStringToOptions(values?.email);

        /** Function used in the creatables component of the Select Emails field. */
        const handleCreateEmail = (email: string): void => {
          let newEmail = email.trim(); // trim whitespace.
          // If new email contains a semi-colon, strip it out and re-add later (for validation)
          if (newEmail.indexOf(";") !== -1) {
            newEmail = newEmail.replace(";", "");
          }
          // Validate new email before adding.
          const regex = new RegExp(emailValidation);
          const emailIsValid = regex.test(newEmail);
          if (emailIsValid) {
            const updatedEmails = hotelForm?.values?.email
              ? `${hotelForm?.values?.email ?? ""}; ${newEmail}`
              : newEmail;
            hotelForm.setFieldValue("email", updatedEmails, false);
          } else {
            setTimeout(() => {
              hotelForm.setFieldError("email", "Email Entered Not Valid");
            }, 0);
          }
          hotelForm.setFieldTouched("email", true);
        };

        const handleDelete = (): void => {
          if (!selectedHotel?._id) {
            return;
          }
          setActiveModal({
            title: "Delete Hotel",
            message: "Are you certain you wish to delete this hotel? This operation cannot be undone.",
            cancel: () => setActiveModal(null),
            confirm: async (): Promise<void> => {
              setActiveModal(null);
              const notifications: INotifications = {
                dispatch: dispatch,
                infoMessage: "Deleting Selected Hotel...",
                successMessage: "Hotel Deleted",
                errorMessage: "Error: Unable To Delete Hotel",
              };

              //TODO Endpoint Return Type is deprecated and needs to be updated.
              interface IOldResponseTypeDelete {
                deleted: boolean;
                document: IHotel;
              }

              const response: IOldResponseTypeDelete = await AmplifyAPIWrapper.Del(
                `/hotel/${selectedHotel?._id}`,
                null,
                notifications
              );
              if (response.deleted) {
                hotelForm.resetForm({ values: initialHotelValues });
                hotelForm.setStatus({ success: true });
                // remove hotel from main list.
                updateHotelList(selectedHotel, "delete");
              } else {
                hotelForm.setStatus({ success: false });
              }
              hotelForm.setSubmitting(false);
            },
            confirmLabel: "Delete",
          });
        };

        return (
          <Form>
            <HotelFormWrapper>
              <Grid container spacing={5}>
                <Grid item xs={6}>
                  <Typography variant="body2">REQUIRED FIELDS</Typography>
                  <FormikTextField label="Name" name="name" />
                  <FormikTextField label="Description" name="description" multiline={true} minRows={1} maxRows={3} />
                  <Grid container wrap="nowrap">
                    <FormikTextField label="Standard Rate" name="standardRate" type="number" />
                    <FormikSelectField
                      name="standardRateCurrency"
                      label=""
                      format={(value: string) => currencyCodes.find((option) => option?.code === value)}
                      parse={(option: ICurrencyCode) => option?.code}
                      getOptionLabel={(option: ICurrencyCode) => `${option.code} - ${option.name}`}
                      getOptionValue={(option: ICurrencyCode) => option.code}
                      options={currencyCodes}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      isSearchable={true}
                      isClearable={false}
                      menuPosition="fixed"
                      maxMenuHeight={200}
                      placeholder="Select Rate Currency..."
                    />
                  </Grid>
                  <FormikMaskTextField
                    className="field textfield hotel switchtime"
                    label="Switch Time (hrs:min)"
                    name="switchTime"
                    patternMask="##:##"
                    format={secondsToHrsMinString}
                    parse={hrsMinString2seconds}
                  />
                  <FormikMaskTextField
                    className="field textfield hotel earlycheckin"
                    label="Early Check-In (hrs:min)"
                    name="earlyCheckin"
                    patternMask="##:##"
                    format={secondsToHrsMinString}
                    parse={hrsMinString2seconds}
                  />
                  <FormikMaskTextField
                    className="field textfield hotel latecheckout"
                    label="Late Check-Out (hrs:min)"
                    name="lateCheckout"
                    patternMask="##:##"
                    format={secondsToHrsMinString}
                    parse={hrsMinString2seconds}
                  />
                  <FormikSelectField
                    className="field selectfield hotel icao"
                    name="icao"
                    label="ICAO"
                    onInputChange={debounce(loadAirportData, 500)}
                    options={airports}
                    getOptionLabel={(airport: IAirportOption) => `${airport.ident} - ${airport.name}`}
                    getOptionValue={(airport: IAirportOption) => airport.ident}
                    format={(ident: string) => airports.find((ap) => ap.ident === ident)}
                    parse={(airport: IAirportOption) => airport?.ident}
                    isSearchable={true}
                    menuPosition={"fixed"}
                    openMenuOnFocus={false}
                    isClearable={true}
                    placeholder="Search Airport..."
                    noOptionsMessage={() => airportSearchString}
                  />
                  <FormikTextField
                    className="field textfield hotel priority"
                    label="Priority"
                    name="priority"
                    type="number"
                  />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2">OPTIONAL FIELDS</Typography>
                  <FormikSelectField
                    className="field selectfield hotel emails"
                    name="email"
                    label="Email(s)"
                    options={emailOptions}
                    isMulti={true}
                    //! values must be converted to {value, label} format to work with creatables.
                    // getOptionLabel={(email: string) => email}
                    // getOptionValue={(email: string) => email}
                    format={(emails: string) => convertSemiColonDelineatedStringToOptions(emails)}
                    // Save selected email options as semi-colon delineated string;
                    parse={(options: IOption[]) => options?.map((option) => option.value).join(";")}
                    isClearable={true}
                    hideSelectedOptions={false}
                    placeholder="To create: Enter email and press enter."
                    onCreateOption={handleCreateEmail}
                    noOptionsMessage={(): any => null}
                  />

                  <FormikPhoneField
                    className="field textfield hotel phonenumber"
                    label="Phone"
                    name="phoneNumber"
                    noCountryCode={true}
                    noMask={true}
                  />
                  <FormikMaskTextField
                    className="field textfield hotel minstay"
                    label="Minimum Stay (hrs:min)"
                    name="minStaySeconds"
                    patternMask="##:##"
                    format={secondsToHrsMinString}
                    parse={hrsMinString2seconds}
                  />
                  <FormikTextField
                    className="field textfield hotel taxrate"
                    label="Tax Rate (%)"
                    name="taxRate"
                    type="number"
                  />
                  <FormikMaskTextField
                    className="field textfield hotel traveltime"
                    label="Airport To Hotel (hrs:min)"
                    name="hotelTravelTime"
                    patternMask="##:##"
                    format={minutesToHrsMinString}
                    parse={hrsMinString2minutes}
                  />
                  <FormikSelectField
                    className="field hotel crewtypes"
                    name="crewType"
                    label="Crew Types"
                    options={crewTypeOptions}
                    isClearable={false}
                    hideSelectedOptions={false}
                    format={(crewType: string) => {
                      return crewType
                        ? crewTypeOptions.find((type) => type.value === crewType)
                        : { label: "All", value: "B" };
                    }}
                    parse={(option: any) => (option?.value ? option?.value : "B")}
                  />
                  <FormikTextField
                    className="field textfield groundtransport"
                    label="Ground Transport"
                    name="groundTransport"
                  />
                  <FormikCheckboxField
                    className="field checkboxfield"
                    label="Allow Booking Management"
                    name="allowBookingManagement"
                  />
                </Grid>

                <Grid container className="form_footer" sx={{ justifyContent: "end  !important" }}>
                  <Button
                    className="btn reset"
                    type="reset"
                    variant="contained"
                    color="secondary"
                    disabled={(!dirty && isValid) || isSubmitting}
                  >
                    Reset
                  </Button>
                  <Button
                    className={"btn save"}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !isValid || !dirty}
                    type="submit"
                  >
                    {selectedHotel ? "Update" : "Create"}
                  </Button>
                  {selectedHotel && (
                    <Button
                      className={"btn delete"}
                      variant="contained"
                      color="secondary"
                      disabled={!selectedHotel || isSubmitting}
                      onClick={handleDelete}
                    >
                      Delete
                    </Button>
                  )}
                </Grid>
              </Grid>
            </HotelFormWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};
